import { useState, useCallback } from 'react';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import type { SimilarDesignsService } from '../../application/services/SimilarDesignsService';
import type { SimilarDesignItem } from '../../domain/models/SimilarDesignItem';

export interface UseSimilarDesignsReturn {
  items: SimilarDesignItem[] | null;
  isLoading: boolean;
  sentinelRef: ReturnType<typeof useIntersectionObserver>['ref'];
}

type SimilarDesignsState = { designId: string; data: SimilarDesignItem[] | null } | null;

export function useSimilarDesigns(
  service: SimilarDesignsService,
  designId: string
): UseSimilarDesignsReturn {
  const [itemsState, setItemsState] = useState<SimilarDesignsState>(null);
  const [loadingDesignId, setLoadingDesignId] = useState<string | null>(null);
  const [fetchedDesignId, setFetchedDesignId] = useState<string | null>(null);

  const items = itemsState?.designId === designId ? itemsState.data : null;
  const isLoading = loadingDesignId === designId;
  const fetched = fetchedDesignId === designId;

  const handleIntersect = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (!entry.isIntersecting || fetched) return;

      const requestDesignId = designId;
      setFetchedDesignId(requestDesignId);
      setLoadingDesignId(requestDesignId);
      service
        .getSimilarDesigns(requestDesignId)
        .then((result) => {
          setItemsState({ designId: requestDesignId, data: result });
        })
        .catch(() => {
          setItemsState({ designId: requestDesignId, data: null });
        })
        .finally(() => {
          setLoadingDesignId((current) => (current === requestDesignId ? null : current));
        });
    },
    [fetched, service, designId]
  );

  const { ref: sentinelRef } = useIntersectionObserver(
    {
      rootMargin: '200px',
      threshold: 0,
      disconnectOnIntersect: true,
      enabled: !fetched,
    },
    handleIntersect
  );

  return { items, isLoading, sentinelRef };
}
